import React, { useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import { Box, IconButton, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';

import { Slot } from 'types/availabilitySlotTypes';
import { useIsMobile } from 'utils/mediaQueries';

interface Props {
  weekday: number;
  label: string;
  slots: Slot[];
  onAddSlot: (slot: Slot) => Promise<void>;
  onRemoveSlot: (slotId: string) => void;
}

const SlotDayCard: React.FC<Props> = ({ weekday, label, slots, onAddSlot, onRemoveSlot }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [newSlot, setNewSlot] = useState<{ start: string; end: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const timeSlots = useMemo(() => {
    const result: string[] = [];
    for (let h = 0; h <= 23; h++) {
      for (let m = 0; m <= 30; m += 30) {
        result.push(dayjs().hour(h).minute(m).second(0).format('HH:mm'));
      }
    }
    return result;
  }, []);

  const isWithinSlot = (t: dayjs.Dayjs, start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    return t.isSame(start) || (t.isAfter(start) && t.isBefore(end)) || t.isSame(end);
  };

  const getDisabledStartTimes = () => {
    return slots.flatMap(slot => {
      const start = dayjs(slot.start_time, 'HH:mm');
      const end = dayjs(slot.end_time, 'HH:mm');
      const times: string[] = [];
      let t = start.clone();
      while (t.isBefore(end)) {
        times.push(t.format('HH:mm'));
        t = t.add(30, 'minute');
      }
      return times;
    });
  };

  const getValidStartTimes = (): string[] => {
    const disabled = new Set(getDisabledStartTimes());
    return timeSlots.filter(t => !disabled.has(t));
  };

  const getValidEndTimes = (start: string): string[] => {
    const [h, m] = start.split(':').map(Number);
    const startTime = dayjs().hour(h).minute(m);
    return timeSlots.filter(t => {
      const endTime = dayjs(t, 'HH:mm');
      const duration = endTime.diff(startTime, 'minute');
      if (duration < 30) return false;

      // Verifica se qualquer ponto entre start e end colide com slot existente
      return !slots.some(slot => {
        const s = dayjs(slot.start_time, 'HH:mm');
        const e = dayjs(slot.end_time, 'HH:mm');
        return (
          isWithinSlot(startTime, s, e) ||
          isWithinSlot(endTime, s, e) ||
          (startTime.isBefore(s) && endTime.isAfter(e))
        );
      });
    });
  };

  const handleStartChange = (start: string) => {
    setNewSlot({ start, end: '' });
    setError(null);
  };

  const handleEndChange = (end: string) => {
    if (!newSlot) return;
    setNewSlot({ ...newSlot, end });

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(async () => {
      if (newSlot.start && end) {
        const slot: Slot = {
          id: '',
          user_id: '', // será atribuído no backend
          weekday,
          start_time: newSlot.start,
          end_time: end,
          slot_duration_minutes: 30,
        };
        await onAddSlot(slot);
        setNewSlot(null);
      }
    }, 1000);
  };

  // Função para formatar horário para HH:mm
  const formatTime = (time: string): string => {
    if (!time) return '';

    // Se já está no formato HH:mm, retorna como está
    if (/^\d{2}:\d{2}$/.test(time)) {
      return time;
    }

    // Se tem segundos (HH:mm:ss), remove os segundos
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time.substring(0, 5);
    }

    // Se é um timestamp ISO ou outro formato, tenta converter
    try {
      const date = new Date(time);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      }
    } catch (error) {
      console.warn('Erro ao formatar horário:', time, error);
    }

    // Fallback: retorna como está
    return time;
  };

  return (
    <Stack
      spacing={1}
      sx={{
        mb: 2,
        py: 1,
        px: 2,
        background: theme.palette.background.paper,
        borderRadius: 2,
      }}
    >
      <Box display="flex" flexDirection="row" alignItems={isMobile ? 'start' : 'start'} gap={2}>
        <Box
          minWidth={isMobile ? 'auto' : '120px'}
          display="flex"
          alignItems={isMobile ? 'start' : 'start'}
          justifyContent={isMobile ? 'center' : 'flex-start'}
          sx={{ textAlign: isMobile ? 'center' : 'left' }}
        >
          <Typography fontWeight={500} mt={1}>
            {label}
          </Typography>
        </Box>
        <Stack spacing={1} flex={1}>
          {slots.length === 0 && !newSlot ? (
            <Box
              display="flex"
              flex={1}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography color="text.secondary">Indisponível</Typography>
              <IconButton
                onClick={() => setNewSlot({ start: '', end: '' })}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light + '20',
                  },
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Box>
          ) : (
            slots.map(slot => (
              <Box
                key={slot.id}
                display="flex"
                alignItems="center"
                gap={1}
                width="100%"
                flexDirection={isMobile ? 'row' : 'row'}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    py: 1,
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 1,
                    width: '80px',
                    flex: isMobile ? 1 : 'none',
                  }}
                >
                  <Typography sx={{ width: '100%', textAlign: 'center' }}>
                    {formatTime(slot.start_time)}
                  </Typography>
                </Stack>
                <Typography variant="body2">-</Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    py: 1,
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 1,
                    width: '80px',
                    flex: isMobile ? 1 : 'none',
                  }}
                >
                  <Typography sx={{ width: '100%', textAlign: 'center' }}>
                    {formatTime(slot.end_time)}
                  </Typography>
                </Stack>
                <IconButton
                  onClick={() => onRemoveSlot(slot.id)}
                  sx={{ ml: 'auto', color: theme.palette.error.main }}
                >
                  <BlockIcon />
                </IconButton>
              </Box>
            ))
          )}

          {slots.length > 0 && !newSlot && (
            <Box display="flex" justifyContent="flex-end">
              <IconButton
                onClick={() => setNewSlot({ start: '', end: '' })}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light + '20',
                  },
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Box>
          )}

          {newSlot && (
            <Box
              display="flex"
              flexDirection={isMobile ? 'row' : 'column'}
              gap={1}
              flexWrap="wrap"
              sx={{ flex: 1, width: '100%' }}
            >
              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={isMobile ? 1 : 0}
                flex={1}
                width={isMobile ? '100%' : 'auto'}
              >
                <Box display="flex" flexDirection="row" gap={1} width="100%">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      py: 1,
                      bgcolor: theme.palette.action.hover,
                      borderRadius: 1,
                      width: '80px',
                      flex: isMobile ? 1 : 'none',
                      justifyContent: 'center',
                    }}
                  >
                    <Select
                      size="small"
                      value={newSlot.start}
                      onChange={e => handleStartChange(e.target.value)}
                      displayEmpty
                      sx={{
                        '& .MuiSelect-select': {
                          p: 0,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Início
                      </MenuItem>
                      {getValidStartTimes().map(t => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                  <Typography variant="body2" alignSelf="center">
                    -
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      py: 1,
                      bgcolor: theme.palette.action.hover,
                      borderRadius: 1,
                      width: '80px',
                      flex: isMobile ? 1 : 'none',
                      justifyContent: 'center',
                    }}
                  >
                    {newSlot.start ? (
                      <Select
                        size="small"
                        value={newSlot.end}
                        onChange={e => handleEndChange(e.target.value)}
                        displayEmpty
                        sx={{
                          '& .MuiSelect-select': {
                            p: 0,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          Fim
                        </MenuItem>
                        {getValidEndTimes(newSlot.start).map(t => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography color="text.secondary" sx={{ px: 1 }}>
                        Fim
                      </Typography>
                    )}
                  </Stack>
                  <IconButton
                    onClick={() => setNewSlot(null)}
                    sx={{
                      color: theme.palette.error.main,
                      ml: 'auto',
                      alignSelf: 'center',
                    }}
                  >
                    <BlockIcon />
                  </IconButton>
                </Box>
                {isMobile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ alignSelf: 'start', mt: 0.5 }}
                  >
                    (Salva automático após 1s)
                  </Typography>
                )}
              </Stack>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'start' }}>
                  (Salva automático após 1s)
                </Typography>
              )}
            </Box>
          )}

          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default SlotDayCard;
